import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Paper, CircularProgress } from '@mui/material';
import HotelSearchForm from '../components/HotelSearch/HotelSearchForm';
import HotelSearchResults from '../components/HotelSearch/HotelSearchResults';
import ImportModal from '../components/HotelSearch/ImportModal';
import { searchHotels, getImportSources } from '../services/hotelService';
import { useAuth } from '../features/auth/context/AuthContext';

const HotelSearchPage: React.FC = () => {
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importSources, setImportSources] = useState<any[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<any>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch available import sources when component mounts
    const fetchImportSources = async () => {
      try {
        const sources = await getImportSources();
        setImportSources(sources);
      } catch (err) {
        console.error('Error fetching import sources:', err);
        setError('Failed to load import sources');
      }
    };

    fetchImportSources();
  }, []);

  const handleSearch = async (searchParams: any) => {
    setLoading(true);
    setError(null);
    try {
      const results = await searchHotels(searchParams);
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching hotels:', err);
      setError('Failed to search hotels. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = (hotel: any) => {
    setSelectedHotel(hotel);
    setIsImportModalOpen(true);
  };

  const handleImportSuccess = (importedHotel: any) => {
    setIsImportModalOpen(false);
    // Navigate to the hotel details page
    navigate(`/hotels/${importedHotel.id}`);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Search Hotels
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <HotelSearchForm onSearch={handleSearch} sources={importSources} />
      </Paper>

      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && searchResults.length > 0 && (
        <HotelSearchResults
          results={searchResults}
          onImport={handleImport}
        />
      )}

      {!loading && searchResults.length === 0 && !error && (
        <Typography variant="body1" color="textSecondary" align="center">
          No hotels found. Try adjusting your search criteria.
        </Typography>
      )}

      <ImportModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        hotel={selectedHotel}
        onSuccess={handleImportSuccess}
      />
    </Container>
  );
};

export default HotelSearchPage;